import * as Sentry from "@sentry/react";
import moment from "moment";
import type { z } from "zod";

import { renameFile } from "../../utils";
import instance from "..";
import { getAttributes, getCustomAttribute, UserActions } from "../auth";
import { MustReauthenticateError } from "../auth/MustReauthenticateError";
import { looseArray, looseParse } from "../looseParse";
import type {
  AccountSubType,
  AccountType,
  InvestmentObjective,
  InvestmentTimeHorizon,
  LiquidityNeeds,
  OptionsLevel,
  RiskTolerance,
} from "./account.constants";
import {
  type accountCashInterestSchema,
  accountSchema,
  activitySchema,
  type assetSchema,
  bankSchema,
  type beneficiarySchema,
  documentRequestResponseSchema,
  type eliteStatusSchema,
  fpslStatusSchema,
  fpslTierSchema,
  marginSchema,
  nonIdentityAccountDocumentSchema,
  nonIdentityAccountDocumentURLSchema,
  optionsApprovalResponseSchema,
  optionsApprovalStatusSchema,
  type orderLegSchema,
  orderSchema,
  ownerDetailsSchema,
  ownerSchema,
  paperAccountMarginSchema,
  paperAccountSchema,
  positionSchema,
  type tradeAccountSchema,
  type tradingTypeSchema,
  trustedContactSchema,
  userConfigurationsSchema,
  watchlistSchema,
  type wireBankStatusSchema,
} from "./account.schema";

export * from "./account.constants";

const isApiObject = (data: unknown): data is Record<string, unknown> =>
  typeof data === "object" && data !== null && !Array.isArray(data);

export type TradeAccount = z.infer<typeof tradeAccountSchema>;

export type UserConfigurations = z.infer<typeof userConfigurationsSchema>;

export type EliteStatus = z.infer<typeof eliteStatusSchema>;

export type AccountCashInterest = z.infer<typeof accountCashInterestSchema>;

export type TradingType = z.infer<typeof tradingTypeSchema>;

export type Account = z.infer<typeof accountSchema>;

export type PaperAccountMargin = z.infer<typeof paperAccountMarginSchema>;

export type Position = z.infer<typeof positionSchema>;

export type OwnerDetails = z.infer<typeof ownerDetailsSchema>;

export type Owner = z.infer<typeof ownerSchema>;

export type TrustedContact = z.infer<typeof trustedContactSchema>;

export type DocumentRequestResponse = z.infer<
  typeof documentRequestResponseSchema
>;

export type DocUploadData = {
  doc_type: string;
  doc_sub_type: string;
  content: File | Blob;
};

export const getLiveAccounts = async () => {
  // Session probe: a 401 here means a dead session, so opt into forced logout.
  const accounts = await instance.get<Account[]>("/accounts", {
    logoutOn401: true,
  });
  return looseParse(
    looseArray(accountSchema, "getLiveAccounts"),
    accounts.data,
    "getLiveAccounts",
  );
};

export const getDetails = async (
  accountID: string,
  product = "live",
): Promise<OwnerDetails> => {
  const res = await instance.get<OwnerDetails>(
    product === "paper" ? "/paper_accounts/" : `/accounts/${accountID}/details`,
  );

  return looseParse(ownerDetailsSchema, res.data, "getDetails");
};

export const patchDetails = async (
  accountID: string,
  payload: Partial<OwnerDetails>,
): Promise<void> => {
  await instance.patch(`/accounts/${accountID}/details`, payload);
};

export type Beneficiary = z.infer<typeof beneficiarySchema>;

export type PostAccountRequest = {
  source: string;
  referral_code: string;
  utm_params: Record<string, string>;
  first_name: string;
  last_name: string;
  country: string;
  version: string;
  crypto_broker: string;
  account_type: AccountType;
  account_sub_type: AccountSubType;
  primary_account_holder_id: string;
  beneficiaries: Beneficiary[];
  investment_time_horizon: InvestmentTimeHorizon;
  investment_objective: InvestmentObjective;
  liquidity_needs: LiquidityNeeds;
  risk_tolerance: RiskTolerance;
};

export type LastOnboardingStep =
  | "signup_step"
  | "address_step"
  | "disclosures_step"
  | "confirmation_step"
  | "kyc_step"
  | "financial_profile_step"
  | "employment_step"
  | "finished";

export const postAccount = async (payload: Partial<PostAccountRequest>) => {
  const res = await instance.post<Account>(`/accounts`, payload);
  return looseParse(accountSchema, res.data, "postAccount");
};

export type PatchAccountRequest = {
  account_name?: string;
  cash_interest?: AccountCashInterest[];
  risk_tolerance?: RiskTolerance;
  liquidity_needs?: LiquidityNeeds;
  investment_objective?: InvestmentObjective;
  investment_time_horizon?: InvestmentTimeHorizon;
};

export const patchAccount = async (
  accountID: string,
  payload: PatchAccountRequest,
): Promise<void> => {
  await instance.patch<OwnerDetails>(`/accounts/${accountID}`, payload);
  return;
};

export const patchEsign = async (
  accountID: string,
  payload: { agreement: string; revision: string; signed_at: Date },
) => {
  const res = await instance.patch<OwnerDetails>(
    `/accounts/${accountID}/details/esign`,
    { esign_audit: [payload] },
  );
  // Empty/204 bodies arrive as "" from Axios; parse an empty object instead of
  // warning on a root invalid_type. Callers only need the mutation to succeed.
  return looseParse(
    ownerDetailsSchema,
    isApiObject(res.data) ? res.data : {},
    "patchEsign",
  );
};

export const getOwners = async (accountID: string): Promise<Owner[]> => {
  const res = await instance.get<{ owners: Owner[] }>(
    `/accounts/${accountID}/owners`,
  );
  return looseParse(
    looseArray(ownerSchema, "getOwners"),
    res.data?.owners,
    "getOwners",
  );
};

export const setUserConfigurations = async (
  accountID: string,
  product = "live",
  payload: Partial<UserConfigurations>,
) => {
  const res = await instance.patch(
    `/${getPathForProduct(product)}/${accountID}/configurations`,
    payload,
  );

  return res.data;
};

export interface UserConfigurationsParams {
  accountID: string;
  product?: string;
}

export const getUserConfigurations = async ({
  accountID = "",
  product = "live",
}: UserConfigurationsParams): Promise<UserConfigurations> => {
  const res = await instance.get<UserConfigurations>(
    `/${getPathForProduct(product)}/${accountID}/configurations`,
  );

  return looseParse(
    userConfigurationsSchema,
    res.data,
    "getUserConfigurations",
  );
};

export const patchFinancialProfile = async (
  accountID: string,
  payload: Partial<OwnerDetails>,
) => {
  const res = await instance.patch<OwnerDetails>(
    `/accounts/${accountID}/steps/next/financial_profile_step`,
    payload,
  );
  return looseParse(ownerDetailsSchema, res.data, "patchFinancialProfile");
};

export const patchEmploymentStatus = async (
  accountID: string,
  payload: Partial<OwnerDetails>,
) => {
  const res = await instance.patch<OwnerDetails>(
    `/accounts/${accountID}/steps/next/employment_step`,
    payload,
  );
  return looseParse(ownerDetailsSchema, res.data, "patchEmploymentStatus");
};

export type Product = "live" | "paper";

export interface LiquidatePositionParams {
  accountID: string;
  product?: Product;
  symbol: string;
  cancelOrders?: boolean;
}

export const liquidatePosition = async ({
  product,
  accountID,
  symbol,
  cancelOrders,
}: LiquidatePositionParams) => {
  if (symbol) {
    const res = await instance.delete(
      `/${
        product === "live" ? "accounts" : "paper_accounts"
      }/${accountID}/positions/${symbol}?cancel_orders=${cancelOrders}`,
    );

    return res.data;
  }
};

export type ExcerciseOptionParams = {
  accountID: string;
  symbol: string;
  product?: string;
};

export const exerciseOption = async ({
  accountID,
  symbol,
  product,
}: ExcerciseOptionParams) => {
  const res = await instance.post(
    `/${
      product === "live" ? "accounts" : "paper_accounts"
    }/${accountID}/positions/${symbol}/exercise`,
  );

  return res.data;
};

export type OrderLeg = z.infer<typeof orderLegSchema>;

export type Order = z.infer<typeof orderSchema>;

export interface CancelOrderParams {
  accountID: string;
  product: string;
  orderID: string;
}

export const cancelOrder = async ({
  product,
  accountID,
  orderID,
}: CancelOrderParams) => {
  const res = await instance.delete(
    `/${
      product === "live" ? "accounts" : "paper_accounts"
    }/${accountID}/orders/${orderID}`,
  );

  return res.data;
};

export interface ReplaceOrderParams {
  accountID: string;
  product: string;
  orderID: string;
  orderDetails: {
    qty?: string;
    time_in_force?: string;
    limit_price?: string;
    stop_price?: string;
    trail?: string;
    client_order_id?: string;
    advanced_instructions?: {
      algorithm?: string;
      destination?: string;
      display_qty?: string;
      start_time?: string;
      end_time?: string;
      max_percentage?: string;
    };
  };
}

export const replaceOrder = async ({
  product,
  accountID,
  orderID,
  orderDetails,
}: ReplaceOrderParams) => {
  const res = await instance.patch(
    `/${
      product === "live" ? "accounts" : "paper_accounts"
    }/${accountID}/orders/${orderID}`,
    orderDetails,
  );

  return res.data;
};

export interface GetOrderParams {
  accountID: string;
  product: string;
  orderID: string;
}

export const getOrder = async ({
  product,
  accountID,
  orderID,
}: GetOrderParams): Promise<Order> => {
  const res = await instance.get(
    `/${
      product === "live" ? "accounts" : "paper_accounts"
    }/${accountID}/orders/${orderID}`,
  );

  return looseParse(orderSchema, res.data, "getOrder");
};

export const getTrustedAccount = async (
  accountID: string,
): Promise<TrustedContact | null> => {
  const res = await instance.get<TrustedContact>(
    `/accounts/${accountID}/trusted_contact`,
    { validateStatus: (status) => status < 400 || status === 404 },
  );
  if (res.status === 404) {
    return null;
  }
  return looseParse(trustedContactSchema, res.data, "getTrustedAccount");
};

export const postTrustedAccount = async (
  accountID: string,
  payload: Partial<TrustedContact>,
) => {
  const res = await instance.post<TrustedContact>(
    `/accounts/${accountID}/trusted_contact`,
    {
      ...payload,
      // todo: fix this, i'll get to it later
      street_address: "",
      city: "",
      state: "",
      postal_code: "",
      country: "",
    },
  );
  return res.data;
};

export type NonIdentityAccountDocumentURL = z.infer<
  typeof nonIdentityAccountDocumentURLSchema
>;

export const getNonIdentityAccountDocumentURL = async (
  accountID: string,
  documentID: string,
): Promise<NonIdentityAccountDocumentURL> => {
  const res = await instance.get(
    `accounts/${accountID}/documents/${documentID}/url`,
    {
      headers: {
        Accept: "*/*",
      },
    },
  );

  return looseParse(
    nonIdentityAccountDocumentURLSchema,
    res.data,
    "getNonIdentityAccountDocumentURL",
  );
};

export type NonIdentityAccountDocument = z.infer<
  typeof nonIdentityAccountDocumentSchema
>;

const DEFAULT_DOCUMENTS_RANGE_IN_YEARS = 3;

export const getNonIdentityAccountDocuments = async (
  accountID: string,
): Promise<Array<NonIdentityAccountDocument>> => {
  const res = await instance.get(
    `accounts/${accountID}/documents`.concat(
      `?start=${moment()
        .subtract(DEFAULT_DOCUMENTS_RANGE_IN_YEARS, "year")
        .format("YYYY-MM-DD")}&end=${moment().format("YYYY-MM-DD")}`,
    ),
  );
  return looseParse(
    looseArray(
      nonIdentityAccountDocumentSchema,
      "getNonIdentityAccountDocuments",
    ),
    res.data,
    "getNonIdentityAccountDocuments",
  );
};

export const getAccountDocuments = async (
  accountID: string,
): Promise<Array<DocumentRequestResponse>> => {
  const res = await instance.get(`documents/accounts/${accountID}`);
  return looseParse(
    looseArray(documentRequestResponseSchema, "getAccountDocuments"),
    res.data,
    "getAccountDocuments",
  );
};

export const getAccountDocumentRequests = async (
  accountID: string,
): Promise<Array<DocumentRequestResponse>> => {
  const res = await instance.get(`documents/accounts/${accountID}/requests`);
  return looseParse(
    looseArray(documentRequestResponseSchema, "getAccountDocumentRequests"),
    res.data,
    "getAccountDocumentRequests",
  );
};

export const uploadAccountDocumentByDocType = async (
  accountID: string,
  payload: DocUploadData,
): Promise<DocumentRequestResponse> => {
  var formData = new FormData();
  formData.append("uploadfile", payload.content);
  const res = await instance.post(
    `documents/accounts/${accountID}/upload/${payload.doc_type}/${payload.doc_sub_type}`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return looseParse(
    documentRequestResponseSchema,
    res.data,
    "uploadAccountDocumentByDocType",
  );
};

export const uploadAccountDocument = async (
  docID: string,
  payload: DocUploadData,
): Promise<DocumentRequestResponse> => {
  const formData = new FormData();
  formData.append("uploadfile", payload.content);
  const res = await instance.post(
    `/documents/${docID}/upload?sub_type=${payload.doc_sub_type}`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return looseParse(
    documentRequestResponseSchema,
    res.data,
    "uploadAccountDocument",
  );
};

export const fetchAccountAgreement = async (
  accountID: string,
  agreement: string,
): Promise<Blob> => {
  const res = await instance.get(
    `/accounts/${accountID}/taxfiles/${agreement}/preview`,
    { responseType: "blob" },
  );
  return res.data;
};

export const requestEmail = async (
  email: string,
  a: string,
): Promise<unknown> => {
  const res = await instance.post("/owner/email", { email, a });
  return res.data;
};

export const confirmEmail = async (code: string): Promise<unknown> => {
  const res = await instance.post("/owner/email-confirm", { code });
  return res.data;
};

export const getCountryOfCitizenship = async () => {
  const user = await UserActions.getCurrentUser();
  const attr = await getAttributes(user);
  return getCustomAttribute(attr, "citizenship", "");
};

// adding a symbol to the watchlist
// POST https://staging-app.tradetalk.us/internal/accounts/b501fb34-56cd-4a2a-80cd-a9a37bf16e10/watchlists:by_name?name=Primary Watchlist
// {"symbol":"GME"}

// removing a symbol from a watchlist
// PUT https://staging-app.tradetalk.us/internal/accounts/b501fb34-56cd-4a2a-80cd-a9a37bf16e10/watchlists:by_name?name=Primary Watchlist
// {"name":"Primary Watchlist","symbols":["SPY","FB"]}

export type Asset = z.infer<typeof assetSchema>;

export type Watchlist = z.infer<typeof watchlistSchema>;

export interface GetWatchlistParams {
  accountID: string;
  name: string;
}

export const getWatchlist = async ({
  accountID,
  name,
}: GetWatchlistParams): Promise<Watchlist> => {
  const res = await instance.get(
    `/accounts/${accountID}/watchlists:by_name?name=${name}`,
  );
  return looseParse(watchlistSchema, res.data, "getWatchlist");
};

export interface AddSymbolToWatchlistParams {
  accountID: string;
  name: string;
  symbol: string;
}

export const addSymbolToWatchlist = async ({
  accountID,
  name,
  symbol,
}: AddSymbolToWatchlistParams): Promise<Watchlist> => {
  const res = await instance.post(
    `/accounts/${accountID}/watchlists:by_name?name=${name}`,
    { symbol },
  );
  return looseParse(watchlistSchema, res.data, "addSymbolToWatchlist");
};

export interface RemoveSymbolFromWatchlistParams {
  accountID: string;
  name: string;
  symbols: string[];
}

export const removeSymbolFromWatchlist = async ({
  accountID,
  name,
  symbols,
}: RemoveSymbolFromWatchlistParams): Promise<Watchlist> => {
  const res = await instance.put(
    `/accounts/${accountID}/watchlists:by_name?name=${name}`,
    { name, symbols },
  );
  return looseParse(watchlistSchema, res.data, "removeSymbolFromWatchlist");
};

export interface CreateWatchlistParams {
  accountID: string;
  name: string;
  symbols: string[];
}

export const createWatchlist = async ({
  accountID,
  name,
  symbols,
}: CreateWatchlistParams): Promise<Watchlist> => {
  const res = await instance.post(`/accounts/${accountID}/watchlists`, {
    name,
    symbols,
  });
  return looseParse(watchlistSchema, res.data, "createWatchlist");
};

export interface GetWatchlistsParams {
  accountID: string;
}

export const getWatchlists = async ({
  accountID,
}: GetWatchlistsParams): Promise<Watchlist[]> => {
  const res = await instance.get(`/accounts/${accountID}/watchlists`);
  return looseParse(
    looseArray(watchlistSchema, "getWatchlists"),
    res.data,
    "getWatchlists",
  );
};

export type PaperAccount = z.infer<typeof paperAccountSchema>;

export const getPaperAccounts = async (): Promise<PaperAccount[]> => {
  // Session probe: a 401 here means a dead session, so opt into forced logout.
  const res = await instance.get(`/paper_accounts`, { logoutOn401: true });
  return looseParse(
    looseArray(paperAccountSchema, "getPaperAccounts"),
    res.data,
    "getPaperAccounts",
  );
};

/**
 * The `/paper_accounts/:id/trade_account/margin` endpoint returns Margin-shaped
 * data, but the app consumes paper accounts as `Account` (via `currentAccount`).
 * This is a known, pre-existing bridge: only the fields the two shapes share
 * (id, account_number, status, currency, balances) are real on a paper account;
 * the rest of `Account` is absent. The margin->Account assertion is centralized
 * here — explicit, greppable, and only reached after the payload is validated by
 * `looseParse` — instead of a bare `as Account[]` at the call site. A full fix
 * requires re-typing paper accounts throughout `AccountContext`.
 */
const toAccountFromPaperMargin = (margin: PaperAccountMargin): Account =>
  margin as unknown as Account;

export const getPaperAccountsWithDetails = async (
  paperAccountIds?: string[],
): Promise<Account[]> => {
  if (!paperAccountIds?.length) {
    return [];
  }

  const promises = await Promise.allSettled(
    paperAccountIds.map((id) =>
      instance.get(`/paper_accounts/${id}/trade_account/margin`),
    ),
  );

  const accounts: Account[] = [];
  const errors: unknown[] = [];
  for (const p of promises) {
    if (p.status === "fulfilled") {
      const margin = looseParse(
        paperAccountMarginSchema,
        p.value.data,
        "getPaperAccountsWithDetails",
      );
      accounts.push(toAccountFromPaperMargin(margin));
    } else {
      errors.push(p.reason);
    }
  }

  if (accounts.length === 0 && errors.length > 0) {
    // Every error being a MustReauthenticateError means the session is dead:
    // the request interceptor throws it (before a request is even sent) when the
    // token exchange fails. A plain margin-endpoint 401 stays a raw axios error
    // because it doesn't opt into logoutOn401, so it can't reach here. Logout is
    // already in flight, so surface the auth error directly instead of a
    // misleading "all fetches failed" aggregate.
    if (errors.every((err) => err instanceof MustReauthenticateError)) {
      throw errors[0];
    }

    const aggregate = new AggregateError(
      errors,
      `All ${errors.length} paper account detail fetches failed`,
    );
    Sentry.captureException(aggregate, {
      level: "error",
      tags: { component: "getPaperAccountsWithDetails" },
      extra: { paperAccountIds, errorCount: errors.length },
    });
    throw aggregate;
  }

  return accounts;
};

export type createPaperAccountParams = {
  name: string;
  cash: number;
};

export const createPaperAccount = async (params: createPaperAccountParams) => {
  const res = await instance.post(`/paper_accounts`, params);
  return res.data;
};

type patchPaperAccountParams = {
  name: string;
};

export const editPaperAccount = async (
  paperAccountId: string,
  params: patchPaperAccountParams,
) => {
  const res = await instance.patch(`/paper_accounts/${paperAccountId}`, params);
  return res.data;
};

export const deletePaperAccount = async (paperAccountId: string) => {
  const res = await instance.delete(`/paper_accounts/${paperAccountId}`);
  return res.data;
};

export type Margin = z.infer<typeof marginSchema>;

export const getMargin = async (
  accountID: string,
  paper?: boolean,
  last?: boolean,
): Promise<Margin | null> => {
  const startPath = paper ? "/paper_accounts" : "/accounts";
  const endPath = last ? "/last" : "";

  try {
    const res = await instance.get(
      `${startPath}/${accountID}/trade_account/margin${endPath}`,
    );

    if (last) {
      // @todo remove mutations when endpoints are refactored
      res.data.last_effective_buying_power =
        res.data.last_buying_power - res.data.last_maintenance_margin;
      res.data.last_excess_margin =
        res.data.last_equity - res.data.last_maintenance_margin;
    }

    return looseParse(marginSchema, res.data, "getMargin");
  } catch (e) {
    console.error(e);
    return null;
  }
};

export interface GetOrdersParams {
  accountID: string;
  paper?: boolean;
  symbols?: string[];
  status?: "open" | "closed" | "all";
  side?: "buy" | "sell" | "sell_short" | "";
  limit?: number;
}

const toCanonicalTimestamp = (value: unknown): string | undefined => {
  if (typeof value !== "string" || value === "") {
    return undefined;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? undefined : new Date(parsed).toISOString();
};

export const getOrders = async ({
  accountID,
  paper,
  symbols,
  status = "all",
  side = "",
  limit = 3000,
}: GetOrdersParams): Promise<Order[]> => {
  const ORDER_LIMIT = Math.min(3000, limit);
  const LIMIT = Math.min(500, ORDER_LIMIT);
  const REQUEST_LIMIT = 10;

  const seen = new Set<string>();
  const allOrders: Order[] = [];
  let fetchDate = new Date(Date.now() + 86_400_000).toISOString();
  let fetching = true;
  let requestCount = 0;

  while (fetching) {
    const res = await instance.get(
      `/${
        paper ? "paper_accounts" : "accounts"
      }/${accountID}/orders?status=${status}&side=${side}&symbols=${symbols?.join(
        ",",
      )}&limit=${LIMIT}&until=${fetchDate}`,
    );

    const orders = looseParse(
      looseArray(orderSchema, "getOrders"),
      res.data ?? [],
      "getOrders",
    );
    for (const order of orders) {
      if (!order.id) {
        allOrders.push(order);
        continue;
      }
      if (!seen.has(order.id)) {
        seen.add(order.id);
        allOrders.push(order);
      }
    }
    requestCount++;

    if (
      orders.length === LIMIT &&
      allOrders.length < ORDER_LIMIT &&
      requestCount < REQUEST_LIMIT
    ) {
      let earliest: string | undefined;
      for (const order of orders) {
        const canonical = toCanonicalTimestamp(order.submitted_at);
        if (canonical && (!earliest || canonical < earliest)) {
          earliest = canonical;
        }
      }

      if (earliest && earliest !== fetchDate) {
        fetchDate = earliest;
      } else {
        fetching = false;
      }
    } else {
      fetching = false;
    }
  }

  return allOrders;
};

export interface GetPositionParams {
  accountID: string;
  symbol: string;
  paper?: boolean;
}

export const getPosition = async ({
  accountID,
  symbol,
  paper,
}: GetPositionParams): Promise<Position> => {
  const res = await instance.get(
    `/${
      paper ? "paper_accounts" : "accounts"
    }/${accountID}/positions/${symbol}`,
  );
  return looseParse(positionSchema, res.data, "getPosition");
};

export interface GetPositionsParams {
  accountID: string;
  product?: string;
}

export const getPositions = async ({
  accountID,
  product = "paper",
}: GetPositionsParams): Promise<Position[]> => {
  const res = await instance.get(
    `/${
      product === "paper" ? "paper_accounts" : "accounts"
    }/${accountID}/positions`,
  );

  return looseParse(
    looseArray(positionSchema, "getPositions"),
    res.data,
    "getPositions",
  );
};

export type Activity = z.infer<typeof activitySchema>;

export interface GetActivitiesParams {
  accountID: string;
  paper?: boolean;
  activityTypes?: string[];
  after?: string;
  date?: string;
  until?: string;
  page_size?: number;
  page_token?: string;
}

export const getActivities = async ({
  accountID,
  paper,
  activityTypes,
  after,
  date,
  until,
  page_size,
  page_token,
}: GetActivitiesParams): Promise<Activity[]> => {
  const base = paper ? "/paper_accounts" : "/accounts";
  const queryParams = {
    activity_types: activityTypes?.join(","),
    after,
    date,
    until,
    page_size,
    page_token,
  };

  const res = await instance.get(`${base}/${accountID}/activities`, {
    params: queryParams,
  });

  return looseParse(
    looseArray(activitySchema, "getActivities"),
    res.data,
    "getActivities",
  );
};

export type WireBankStatus = z.infer<typeof wireBankStatusSchema>;

export type Bank = z.infer<typeof bankSchema>;

export interface GetBanksParams {
  accountID: string;
}

export const getBanks = async ({
  accountID,
}: GetBanksParams): Promise<Bank[]> => {
  const res = await instance.get(`/accounts/${accountID}/banks`);
  return looseParse(looseArray(bankSchema, "getBanks"), res.data, "getBanks");
};

export interface CreateBankParams {
  accountID: string;
  bank: Bank;
}

export const createBank = async ({
  accountID,
  bank,
}: CreateBankParams): Promise<Bank> => {
  const res = await instance.post(`/accounts/${accountID}/banks`, bank);
  return looseParse(bankSchema, res.data, "createBank");
};

export const uploadBankStatement = async ({
  accountID,
  file,
}: {
  accountID: string;
  file: File;
}): Promise<DocumentRequestResponse> => {
  const now = moment().format("YYYY-MM-DD HH:mm:ss");
  const bankStatement = renameFile(file, `bank_statement_${now}`);
  const formData = new FormData();
  formData.append("uploadfile", bankStatement);

  const res = await instance.post(
    `/documents/accounts/${accountID}/upload/identity_verification/bank_statement`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return looseParse(
    documentRequestResponseSchema,
    res.data,
    "uploadBankStatement",
  );
};

export interface DeleteBankParams {
  accountID: string;
  bankID: string;
}

export const deleteBank = async ({
  accountID,
  bankID,
}: DeleteBankParams): Promise<void> => {
  const res = await instance.delete(`/accounts/${accountID}/banks/${bankID}`);
  return res.data;
};

const getPathForProduct = (product: string) => {
  return product === "paper" ? "paper_accounts" : "accounts";
};

export const getIntercomUserHash = async (): Promise<string> => {
  const res = await instance.get<{ intercom_id: string }>(`/owner/intercom-id`);

  return res.data.intercom_id;
};

export type OptionsApprovalStatus = z.infer<typeof optionsApprovalStatusSchema>;

export const getOptionsApprovalStatus = async (
  accountId: string,
): Promise<OptionsApprovalStatus> => {
  const res = await instance.get<OptionsApprovalStatus>(
    `/accounts/${accountId}/options/approval`,
  );
  return looseParse(
    optionsApprovalStatusSchema,
    res.data,
    "getOptionsApprovalStatus",
  );
};

export type OptionsApprovalResponse = z.infer<
  typeof optionsApprovalResponseSchema
>;

export const postOptionsApproval = async (
  accountId: string,
  level: OptionsLevel,
): Promise<OptionsApprovalResponse> => {
  const res = await instance.post<OptionsApprovalResponse>(
    `/accounts/${accountId}/options/approval`,
    {
      level,
    },
  );
  return looseParse(
    optionsApprovalResponseSchema,
    res.data,
    "postOptionsApproval",
  );
};

export const enrollInFPSL = async (accountID: string): Promise<void> => {
  await instance.post(`/accounts/${accountID}/fpsl/US`);
};

export const unenrollInFPSL = async (accountID: string): Promise<void> => {
  await instance.delete(`/accounts/${accountID}/fpsl/US`);
};

export type FPSLTier = z.infer<typeof fpslTierSchema>;

export const getFPSLTiers = async (accountID: string): Promise<FPSLTier[]> => {
  const res = await instance.get(`/accounts/${accountID}/fpsl/US/tiers`);
  return looseParse(
    looseArray(fpslTierSchema, "getFPSLTiers"),
    res.data,
    "getFPSLTiers",
  );
};

export type FPSLStatus = z.infer<typeof fpslStatusSchema>;

export const getFPSLStatus = async (accountID: string): Promise<FPSLStatus> => {
  const res = await instance.get(`/accounts/${accountID}/fpsl/US`);
  return looseParse(fpslStatusSchema, res.data, "getFPSLStatus");
};
